<template>
  <div class="page-shell home-mode plaza-mode">
    <div class="home-ambient home-ambient-left"></div>
    <div class="home-ambient home-ambient-right"></div>

    <div class="app-shell">
      <AppSidebar />

      <main class="plaza-shell">
        <header class="plaza-hero">
          <p class="studio-eyebrow">Public Gallery</p>
          <h1>作品广场</h1>
          <p>这里展示所有公开的图片与视频作品，点击卡片即可在弹出层中预览。</p>
        </header>

        <section class="plaza-summary">
          <article class="plaza-stat">
            <strong>{{ squareItems.length }}</strong>
            <span>公开作品</span>
          </article>
          <article class="plaza-stat">
            <strong>{{ imageCount }}</strong>
            <span>公开图片</span>
          </article>
          <article class="plaza-stat">
            <strong>{{ videoCount }}</strong>
            <span>公开视频</span>
          </article>
        </section>

        <section class="plaza-panel">
          <div class="studio-block-title">
            <span>最新公开作品</span>
          </div>

          <div v-if="loading" class="studio-history-empty plaza-empty">
            正在加载广场内容...
          </div>

          <div v-else-if="statusText" class="studio-history-empty plaza-empty">
            {{ statusText }}
          </div>

          <div v-else-if="!squareItems.length" class="studio-history-empty plaza-empty">
            还没有公开作品，先去图片或视频模块把作品设为公开吧。
          </div>

          <div v-else class="plaza-grid">
            <button
              v-for="item in squareItems"
              :key="item.type + ':' + item.id"
              type="button"
              class="plaza-card plaza-card-trigger"
              @click="openPreview(item)"
            >
              <div v-if="item.type === 'image'" class="plaza-card-media plaza-card-media-image">
                <img :src="item.src" :alt="item.title">
              </div>

              <div v-else class="plaza-card-media plaza-card-media-video">
                <img
                  v-if="item.coverUrl"
                  :src="item.coverUrl"
                  :alt="item.title"
                >
                <div v-else class="plaza-video-fallback" aria-hidden="true">
                  <span>Video</span>
                </div>
                <span class="plaza-video-play" aria-hidden="true"></span>
              </div>

              <div class="plaza-card-copy">
                <div class="plaza-card-top">
                  <strong>{{ item.title }}</strong>
                  <span>{{ item.type === "image" ? "图片" : "视频" }}</span>
                </div>
                <p>{{ item.prompt || "暂未填写描述" }}</p>
                <small>{{ formatDate(item.updatedAt || item.createdAt) }}</small>
              </div>
            </button>
          </div>
        </section>
      </main>

      <div
        v-if="previewItem"
        class="studio-lightbox"
        @click.self="closePreview"
      >
        <button
          type="button"
          class="studio-lightbox-close"
          @click="closePreview"
        >
          Close
        </button>

        <div class="studio-lightbox-panel">
          <div class="studio-lightbox-media">
            <img
              v-if="previewItem.type === 'image'"
              class="studio-lightbox-image"
              :src="previewItem.src"
              :alt="previewItem.title"
            >

            <template v-else>
              <video
                class="plaza-lightbox-video"
                controls
                playsinline
                preload="metadata"
                :key="previewItem.videoUrl"
                @error="previewVideoError = true"
              >
                <source :src="previewItem.videoUrl" :type="resolveVideoMimeType(previewItem.videoUrl)">
                This browser cannot play this video.
              </video>
              <div v-if="previewVideoError" class="studio-video-error plaza-video-error">
                <span>This browser cannot play this video inline. The video codec may be incompatible.</span>
                <a :href="previewItem.videoUrl" target="_blank" rel="noopener noreferrer">Open video in new window</a>
              </div>
              <a
                class="studio-video-download plaza-video-download"
                :href="previewItem.videoUrl"
                :download="buildVideoDownloadName(previewItem)"
                target="_blank"
                rel="noopener noreferrer"
              >
                Download video
              </a>
            </template>
          </div>

          <div class="studio-lightbox-copy">
            <strong>{{ previewItem.title }}</strong>
            <span>{{ previewItem.type === "image" ? "图片作品" : "视频作品" }}</span>
            <p>{{ previewItem.prompt || "暂未填写描述" }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AppSidebar from "../components/AppSidebar.vue";
import { refreshToken } from "../api/auth";
import { getSquare } from "../api/ai";
import { clearAuthState, readAuthState, saveAuthState } from "../utils/auth";

export default {
  name: "PlazaView",
  components: {
    AppSidebar: AppSidebar
  },
  data: function data() {
    return {
      auth: readAuthState(),
      loading: false,
      statusText: "",
      squareItems: [],
      previewItem: null,
      previewVideoError: false
    };
  },
  computed: {
    imageCount: function imageCount() {
      return this.squareItems.filter(function(item) {
        return item.type === "image";
      }).length;
    },
    videoCount: function videoCount() {
      return this.squareItems.filter(function(item) {
        return item.type === "video";
      }).length;
    }
  },
  created: function created() {
    this.loadSquare();
  },
  methods: {
    async loadSquare() {
      var self = this;
      this.loading = true;
      this.statusText = "";
      this.auth = readAuthState();

      try {
        await this.runWithAuthRetry(async function() {
          var payload = await getSquare({
            limit: 100
          }, self.auth.accessToken);
          var source = unwrapPayload(payload);
          self.squareItems = normalizeSquareCollection(source);
        });
      } catch (error) {
        this.statusText = error.message || "广场加载失败，请稍后重试。";
      } finally {
        this.loading = false;
      }
    },
    openPreview: function openPreview(item) {
      this.previewItem = item || null;
      this.previewVideoError = false;
    },
    closePreview: function closePreview() {
      this.previewItem = null;
      this.previewVideoError = false;
    },
    resolveVideoMimeType: function resolveVideoMimeTypeMethod(url) {
      return resolveVideoMimeType(url);
    },
    buildVideoDownloadName: function buildVideoDownloadNameMethod(item) {
      return buildVideoDownloadName(item);
    },
    async runWithAuthRetry(task) {
      try {
        return await task();
      } catch (error) {
        if (error.status !== 401) {
          throw error;
        }

        var refreshed = await this.tryRefresh();
        if (!refreshed) {
          this.handleUnauthorized();
          throw error;
        }

        return task();
      }
    },
    async tryRefresh() {
      try {
        var payload = await refreshToken(this.auth.accessToken);
        this.auth = saveAuthState({
          accessToken: payload.access_token,
          tokenType: payload.token_type,
          expiresIn: payload.expires_in,
          user: payload.user
        });
        return true;
      } catch (error) {
        return false;
      }
    },
    handleUnauthorized: function handleUnauthorized() {
      clearAuthState();
      this.$router.replace({ name: "login" });
    },
    formatDate: function formatDate(value) {
      if (!value) {
        return "刚刚公开";
      }

      var date = new Date(value);

      if (Number.isNaN(date.getTime())) {
        return value;
      }

      return date.toLocaleString("zh-CN", {
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit"
      });
    }
  }
};

function unwrapPayload(payload) {
  if (payload && payload.data) {
    return payload.data;
  }

  return payload || {};
}

function normalizeSquareItem(payload) {
  var source = payload || {};
  var type = resolveSquareType(source);

  if (!source.id || !type) {
    return null;
  }

  return {
    id: source.id,
    type: type,
    title: source.title || source.prompt || (type === "image" ? "公开图片" : "公开视频"),
    prompt: source.prompt || source.description || "",
    src: normalizeRemoteUrl(source.imageUrl || source.coverUrl || source.src || ""),
    coverUrl: normalizeRemoteUrl(source.coverUrl || source.imageUrl || source.src || ""),
    videoUrl: normalizeRemoteUrl(source.videoUrl || ""),
    createdAt: source.createdAt || "",
    updatedAt: source.updatedAt || source.createdAt || ""
  };
}

function normalizeSquareCollection(payload) {
  var source = payload || {};
  var images = Array.isArray(source.images) ? source.images : [];
  var videos = Array.isArray(source.videos) ? source.videos : [];
  var directList = Array.isArray(source) ? source : (Array.isArray(source.data) ? source.data : []);

  if (images.length || videos.length) {
    return images.concat(videos).map(normalizeSquareItem).filter(Boolean).sort(sortSquareItems);
  }

  return directList.map(normalizeSquareItem).filter(Boolean).sort(sortSquareItems);
}

function sortSquareItems(a, b) {
  return String(b.updatedAt || b.createdAt || "").localeCompare(String(a.updatedAt || a.createdAt || ""));
}

function resolveSquareType(source) {
  if (source.type === "image" || source.kind === "image") {
    return "image";
  }

  if (source.type === "video" || source.kind === "video") {
    return "video";
  }

  if (source.videoUrl) {
    return "video";
  }

  if (source.imageUrl || source.coverUrl || source.src) {
    return "image";
  }

  return "";
}

function normalizeRemoteUrl(url) {
  if (!url) {
    return "";
  }

  return String(url);
}

function resolveVideoMimeType(url) {
  var cleanUrl = String(url || "").split("?")[0].split("#")[0].toLowerCase();

  if (/\.webm$/.test(cleanUrl)) {
    return "video/webm";
  }

  if (/\.ogg$|\.ogv$/.test(cleanUrl)) {
    return "video/ogg";
  }

  if (/\.m3u8$/.test(cleanUrl)) {
    return "application/vnd.apple.mpegurl";
  }

  if (/\.mov$/.test(cleanUrl)) {
    return "video/quicktime";
  }

  return "video/mp4";
}

function buildVideoDownloadName(item) {
  var source = item || {};
  var title = String(source.title || source.prompt || "video")
    .replace(/[\\/:*?"<>|]+/g, "-")
    .replace(/\s+/g, "-")
    .slice(0, 40);
  var cleanUrl = String(source.videoUrl || "").split("?")[0].split("#")[0].toLowerCase();
  var extensionMatch = cleanUrl.match(/\.([a-z0-9]+)$/);
  var extension = extensionMatch ? extensionMatch[1] : "mp4";

  return title + "." + extension;
}
</script>
